import { Fragment, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Field, reduxForm, reset } from 'redux-form';
import { Box, Tab, Tabs } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import _ from 'lodash';
import { userInviteActions } from '_actions';
import { getCurrentUser, isValidEmail, required } from '_helpers';
import { SnackBar } from 'components/common';
import BasicModal from 'components/v2/views/Onboarding/Utilities/BasicModal';
import BasicFormTextField from 'components/v2/common/BasicFormTextField';
import ActionButton from 'components/v2/common/ActionButton';
import BasicActionButton from 'components/v3/common/BasicActionButton';
import { buildCreatePayload } from 'components/v2/views/UserManagement/Utilities/UserInviteUtils';
import useRolesFetch from 'components/v2/views/Proactive/Utilities/hooks/useRolesFetch';

const FORM_NAME = 'UsersInviteModalForm';
const COMPANY_ADMIN_ROLE_AUTHORITY = 'ROLE_COMPANY_ADMIN';
const COMPANY_ADMIN_ROLE_LABEL = 'Company Admin';

export function useUsersInviteModal() {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const initiateUsersInviteModal = () => handleOpen();

  return {
    initiateUsersInviteModal,
    modalProps: { open, handleOpen, handleClose },
  };
}

function UsersInviteModal({
  handleSubmit,
  open,
  handleOpen,
  handleClose,
  onInviteCreated,
}) {
  const dispatch = useDispatch();
  const currentUser = getCurrentUser();
  const organizationId = currentUser?.organization_id || null;

  const [activeTab, setActiveTab] = useState('new');
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const { roles } = useRolesFetch();
  const companyAdminRoleId = useMemo(
    () => roles?.find((role) => role.name === COMPANY_ADMIN_ROLE_AUTHORITY)?.id,
    [roles]
  );

  const loadingCreate = useSelector((state) => state.userInvite.loadingCreate);
  const createInviteError = useSelector(
    (state) => state.userInvite.createInviteError
  );

  useEffect(() => {
    if (!open) return;
    dispatch(reset(FORM_NAME));
    setActiveTab('new');
  }, [open, dispatch]);

  useEffect(() => {
    if (!pendingSubmit || loadingCreate) return;

    setPendingSubmit(false);

    if (!_.isEmpty(createInviteError)) return;

    dispatch(reset(FORM_NAME));
    handleClose();
    if (typeof onInviteCreated === 'function') onInviteCreated();
  }, [pendingSubmit, loadingCreate, createInviteError]);

  const handleTabChange = (_event, value) => {
    setActiveTab(value);
    dispatch(reset(FORM_NAME));
  };

  const handleCreate = (values) => {
    if (_.isEmpty(values)) return;

    if (!isValidEmail(values?.email)) {
      SnackBar.showMessage('Invalid email');
      return;
    }

    if (!companyAdminRoleId) {
      SnackBar.showMessage('Roles are still loading, please try again');
      return;
    }

    const payload = buildCreatePayload({
      ...values,
      role: companyAdminRoleId,
      organization: organizationId,
    });

    setPendingSubmit(true);
    dispatch(userInviteActions.create(payload));
  };

  const renderCheckbox = ({ input, label }) => (
    <FormControlLabel
      control={
        <Checkbox
          checked={!!input.checked}
          onChange={input.onChange}
        />
      }
      label={label}
    />
  );

  return (
    <Fragment>
      <BasicModal
        open={open}
        handleClose={handleClose}
        handleOpen={handleOpen}
        title={'Invite user'}
      >
        <form
          name={FORM_NAME}
          onSubmit={handleSubmit(handleCreate)}
          autoComplete="off"
        >
          <Box sx={{ borderBottom: '1px solid #CED4D4' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
            >
              <Tab label="New User" value="new" />
              <Tab label="Existing User" value="existing" />
            </Tabs>
          </Box>

          <Box sx={styles.body}>
            <Box sx={styles.fieldRow}>
              <Box sx={styles.inputDesc}>Email</Box>
              <Field
                name="email"
                component={BasicFormTextField}
                placeholder={'Enter email'}
                validate={required}
              />
            </Box>

            {activeTab === 'new' && (
              <Fragment>
                <Box sx={styles.fieldRow}>
                  <Box sx={styles.inputDesc}>First Name</Box>
                  <Field
                    name="firstName"
                    component={BasicFormTextField}
                    placeholder={'Enter first name'}
                    validate={required}
                  />
                </Box>
                <Box sx={styles.fieldRow}>
                  <Box sx={styles.inputDesc}>Last Name</Box>
                  <Field
                    name="lastName"
                    component={BasicFormTextField}
                    placeholder={'Enter last name'}
                    validate={required}
                  />
                </Box>
              </Fragment>
            )}

            <Box sx={styles.fieldRow}>
              <Box sx={styles.inputDesc}>Role</Box>
              <BasicFormTextField
                input={{ value: COMPANY_ADMIN_ROLE_LABEL }}
                meta={{}}
                disabled
              />
            </Box>

            {activeTab === 'existing' && (
              <Box sx={styles.fieldRow}>
                <Box sx={styles.inputDesc}>Auto-Accept</Box>
                <Field
                  name="autoAccept"
                  component={renderCheckbox}
                  type="checkbox"
                  label="Accept the invite automatically"
                />
              </Box>
            )}
          </Box>

          <Box sx={styles.footer}>
            <Box sx={{ width: '150px' }}>
              <ActionButton
                label={'Send invite'}
                disabled={loadingCreate || !companyAdminRoleId}
              />
            </Box>
            <Box sx={{ width: '120px', ml: '18px' }}>
              <BasicActionButton
                type={'button'}
                label={'Cancel'}
                action={handleClose}
                buttonBordered
                disabled={loadingCreate}
              />
            </Box>
          </Box>
        </form>
      </BasicModal>
    </Fragment>
  );
}

const styles = {
  body: {
    p: '20px 4px 24px 4px',
  },
  fieldRow: {
    mb: '15px',
  },
  inputDesc: {
    fontFamily: 'LarsseitMedium',
    fontSize: '14px',
    fontWeight: 500,
    color: '#495D5E',
    mb: '7px',
  },
  footer: {
    borderTop: '1px solid #CED4D4',
    pt: '16px',
    display: 'flex',
    alignItems: 'center',
  },
};

export default reduxForm({
  form: FORM_NAME,
  enableReinitialize: false,
})(UsersInviteModal);
